/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Jobs Service - SuperAdmin Scheduled Jobs API
 * ============================================================================
 *
 * Service layer for Scheduled Jobs Dashboard API calls.
 *
 * ============================================================================
 */

import { get, post } from '@/services/api/client';
import { ApiResponse, PagedResponse } from '@/services/api/types';

const BASE_URL = '/admin/jobs';

// Types
export type ExecutionStatus = 'RUNNING' | 'SUCCESS' | 'FAILED' | 'SKIPPED';

export interface JobStats {
  successCount24h: number;
  failureCount24h: number;
  avgDurationMs: number | null;
  lastStatus: ExecutionStatus | null;
}

export interface ScheduledJob {
  id: string;
  jobName: string;
  description: string | null;
  scheduleDescription: string;
  cronExpression: string | null;
  fixedRateMs: number | null;
  beanName: string;
  methodName: string;
  enabled: boolean;
  paused: boolean;
  lastRunAt: string | null;
  nextRunAt: string | null;
  stats: JobStats;
}

export interface JobExecution {
  id: string;
  jobName: string;
  startedAt: string;
  finishedAt: string | null;
  durationMs: number | null;
  status: ExecutionStatus;
  triggeredBy: 'SCHEDULER' | 'MANUAL' | 'SYSTEM';
  errorMessage: string | null;
  itemsProcessed: number | null;
}

export interface JobDashboardOverview {
  totalJobs: number;
  activeJobs: number;
  pausedJobs: number;
  executionsToday: number;
  failuresToday: number;
  successRate: number;
  recentExecutions: Array<{
    jobName: string;
    startedAt: string;
    durationMs: number;
    status: ExecutionStatus;
  }>;
}

/**
 * Jobs Service
 */
export const jobsService = {
  /**
   * Get all scheduled jobs
   */
  async getAllJobs(): Promise<ScheduledJob[]> {
    const response = await get<ApiResponse<ScheduledJob[]>>(BASE_URL);
    return response.data;
  },

  /**
   * Get dashboard overview
   */
  async getDashboardOverview(): Promise<JobDashboardOverview> {
    const response = await get<ApiResponse<JobDashboardOverview>>(`${BASE_URL}/overview`);
    return response.data;
  },

  /**
   * Get a specific job by name
   */
  async getJob(name: string): Promise<ScheduledJob> {
    const response = await get<ApiResponse<ScheduledJob>>(`${BASE_URL}/${name}`);
    return response.data;
  },

  /**
   * Get execution history for a job
   */
  async getJobHistory(
    name: string,
    page: number = 0,
    size: number = 20
  ): Promise<PagedResponse<JobExecution>> {
    const response = await get<ApiResponse<PagedResponse<JobExecution>>>(
      `${BASE_URL}/${name}/history`,
      { page, size }
    );
    return response.data;
  },

  /**
   * Get recent executions across all jobs
   */
  async getRecentExecutions(
    page: number = 0,
    size: number = 20
  ): Promise<PagedResponse<JobExecution>> {
    const response = await get<ApiResponse<PagedResponse<JobExecution>>>(
      `${BASE_URL}/executions`,
      { page, size }
    );
    return response.data;
  },

  /**
   * Manually trigger a job
   */
  async triggerJob(name: string): Promise<void> {
    await post<ApiResponse<void>>(`${BASE_URL}/${name}/trigger`, {});
  },

  /**
   * Pause a job
   */
  async pauseJob(name: string): Promise<ScheduledJob> {
    const response = await post<ApiResponse<ScheduledJob>>(`${BASE_URL}/${name}/pause`, {});
    return response.data;
  },

  /**
   * Resume a paused job
   */
  async resumeJob(name: string): Promise<ScheduledJob> {
    const response = await post<ApiResponse<ScheduledJob>>(`${BASE_URL}/${name}/resume`, {});
    return response.data;
  },
};

export default jobsService;
